import * as React from 'react';
import { api, ApiError, getToken } from '../api-client';

export interface PlatformBranding {
  name: string;
  logoUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string;
  supportEmail: string | null;
}

export interface TenantBranding {
  company_name: string;
  company_logo_url: string | null;
  company_accent_color: string | null;
  company_address: string | null;
  company_tagline: string | null;
  company_phone: string | null;
  company_email: string | null;
  company_website: string | null;
  default_invoice_due_days: number;
  default_tax_rate: string;
  invoice_number_prefix: string;
  owner_notification_email: string | null;
  owner_notification_mode: 'instant' | 'hourly' | 'daily' | 'off';
  /** Workspace IANA timezone (default 'UTC'). */
  timezone?: string;
}

interface BrandingContextValue {
  platform: PlatformBranding | null;
  tenant: TenantBranding | null;
  loading: boolean;
  error: string | null;
  reloadTenant: () => void;
}

const BrandingContext = React.createContext<BrandingContextValue | null>(null);

const FALLBACK_PLATFORM: PlatformBranding = {
  name: 'Platform',
  logoUrl: null,
  faviconUrl: null,
  primaryColor: '#0066FF',
  supportEmail: null,
};

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const [platform, setPlatform] = React.useState<PlatformBranding | null>(null);
  const [tenant, setTenant] = React.useState<TenantBranding | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [tenantTick, setTenantTick] = React.useState(0);
  const [authTick, setAuthTick] = React.useState(0);

  // Watch for auth-token changes (login/logout) so we can refetch tenant.
  React.useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'auth_token') setAuthTick((t) => t + 1);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Platform branding (always; public endpoint).
  React.useEffect(() => {
    let cancelled = false;
    api<{ platform: PlatformBranding }>('/api/branding')
      .then((res) => { if (!cancelled) setPlatform(res.platform); })
      .catch((err: ApiError) => {
        if (!cancelled) { setPlatform(FALLBACK_PLATFORM); setError(err.message); }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Tenant settings (only when authenticated as a tenant user).
  React.useEffect(() => {
    setError(null);
    const token = getToken();
    if (!token) { setTenant(null); return; }

    // Peek at the JWT payload (no verification — purely to skip the call
    // for super-admins, who would 403 and clutter the console).
    if (peekTokenType(token) !== 'user') {
      setTenant(null);
      return;
    }

    let cancelled = false;
    api<{ settings: TenantBranding }>('/api/tenant/settings', { noRedirectOn401: true })
      .then((res) => { if (!cancelled) setTenant(res.settings); })
      .catch((err: ApiError) => {
        // 401/403 here means token was rotated mid-session. Silent.
        if (!cancelled && err.status !== 401 && err.status !== 403) {
          setError(err.message);
        }
        if (!cancelled) setTenant(null);
      });
    return () => { cancelled = true; };
  }, [tenantTick, authTick]);

  // Apply tab title + favicon from platform.
  React.useEffect(() => {
    if (!platform) return;
    document.title = platform.name;
    if (platform.faviconUrl) {
      let link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
      if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
      link.href = platform.faviconUrl;
    }
  }, [platform]);

  const reloadTenant = React.useCallback(() => setTenantTick((t) => t + 1), []);

  const value: BrandingContextValue = { platform, tenant, loading, error, reloadTenant };
  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding(): BrandingContextValue {
  const v = React.useContext(BrandingContext);
  if (!v) throw new Error('useBranding must be used inside <BrandingProvider>');
  return v;
}

/**
 * Read the `type` claim from a JWT without verifying the signature.
 * Used only to decide whether to make the tenant-settings request;
 * the server still verifies on every protected call.
 */
function peekTokenType(token: string): string | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof decoded?.type === 'string' ? decoded.type : null;
  } catch {
    return null;
  }
}
